import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import AgentEditorPage from "./pages/AgentEditorPage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import ConversationsPage from "./pages/ConversationsPage";
import CalendarPage from "./pages/CalendarPage";
import ChatTestPage from "./pages/ChatTestPage";
import WhatsAppConfigPage from "./pages/WhatsAppConfigPage";
import WhatsAppSetupPage from "./pages/WhatsAppSetupPage";
import TemplatesPage from "./pages/TemplatesPage";
import PhoneAssignmentsPage from "./pages/PhoneAssignmentsPage";
import UsersAdminPage from "./pages/UsersAdminPage";
import AdminLogsPage from "./pages/AdminLogsPage";
import LeadsPage from "./pages/LeadsPage";
import ErrorBoundary from "./components/ErrorBoundary";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(storedToken);
          } else {
            localStorage.removeItem("token");
            setToken(null);
          }
        } catch (e) {
          console.error("Auth check failed:", e);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return { user, token, loading, login, logout, setUser };
};

// Protected Route component
const ProtectedRoute = ({ children, auth }) => {
  if (auth.loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Admin Route component
const AdminRoute = ({ children, auth }) => {
  if (auth.loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!auth.token || auth.user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Reinicia el ErrorBoundary al cambiar de ruta, para no dejar al usuario
// atrapado en la pantalla de error tras un fallo de render en una página anterior.
const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
};

function App() {
  const auth = useAuth();

  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <RouteErrorBoundary>
        <Routes>
          <Route path="/" element={<Navigate to={auth.token ? "/dashboard" : "/login"} />} />
          <Route path="/login" element={auth.token ? <Navigate to="/dashboard" /> : <LoginPage auth={auth} />} />
          <Route path="/register" element={auth.token ? <Navigate to="/dashboard" /> : <RegisterPage auth={auth} />} />
          <Route path="/dashboard" element={
            <ProtectedRoute auth={auth}>
              <DashboardPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/agents/new" element={
            <ProtectedRoute auth={auth}>
              <AgentEditorPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/agents/:agentId" element={
            <ProtectedRoute auth={auth}>
              <AgentEditorPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/agents/:agentId/knowledge" element={
            <ProtectedRoute auth={auth}>
              <KnowledgeBasePage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/agents/:agentId/conversations" element={
            <ProtectedRoute auth={auth}>
              <ConversationsPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/agents/:agentId/chat" element={
            <ProtectedRoute auth={auth}>
              <ChatTestPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/calendar" element={
            <ProtectedRoute auth={auth}>
              <CalendarPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/whatsapp-config" element={
            <ProtectedRoute auth={auth}>
              <WhatsAppConfigPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/whatsapp-setup" element={
            <ProtectedRoute auth={auth}>
              <WhatsAppSetupPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/templates" element={
            <ProtectedRoute auth={auth}>
              <TemplatesPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/phone-assignments" element={
            <ProtectedRoute auth={auth}>
              <PhoneAssignmentsPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <AdminRoute auth={auth}>
              <UsersAdminPage auth={auth} />
            </AdminRoute>
          } />
          <Route path="/admin/logs" element={
            <AdminRoute auth={auth}>
              <AdminLogsPage auth={auth} />
            </AdminRoute>
          } />
          <Route path="/leads" element={
            <ProtectedRoute auth={auth}>
              <LeadsPage auth={auth} />
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </RouteErrorBoundary>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
